import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from '../../utils/constants';
import './UserMentionDropdown.css';

const UserMentionDropdown = ({ 
    isVisible, 
    searchTerm, 
    onSelectUser, 
    onClose, 
    position,
    currentUserId 
}) => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const dropdownRef = useRef(null);

    // Debug logging
    // console.log('UserMentionDropdown props:', { isVisible, searchTerm, position, currentUserId });

    useEffect(() => {
        if (!isVisible || !currentUserId) {
            setUsers([]);
            return;
        }

        const fetchUsers = async () => {
            setIsLoading(true);
            try {
                console.log('Fetching users with params:', { currentUserId, searchTerm });
                const params = new URLSearchParams({ 
                    userId: currentUserId, 
                    term: searchTerm || '' // Allow empty search term to get all users
                });
                const response = await fetch(`${API_URL}/api/connections/search-users?${params.toString()}`);
                console.log('API response status:', response.status);
                if (response.ok) {
                    const data = await response.json();
                    console.log('API response data:', data);
                    setUsers(data.slice(0, 5)); // Limit to 5 results
                } else {
                    console.log('API response not ok:', response.statusText);
                    setUsers([]);
                }
            } catch (error) {
                console.error('Error fetching users:', error);
                setUsers([]);
            } finally {
                setIsLoading(false);
            }
        };

        const debounceTimer = setTimeout(fetchUsers, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchTerm, currentUserId, isVisible]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [users]);

    const handleUserSelect = (selectedUser) => {
        onSelectUser(selectedUser);
        onClose(); // Close dropdown after selection
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isVisible || users.length === 0) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex(prev => (prev + 1) % users.length);
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex(prev => (prev - 1 + users.length) % users.length);
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (users[selectedIndex]) {
                        handleUserSelect(users[selectedIndex]);
                    }
                    break;
                case 'Escape':
                    e.preventDefault();
                    onClose();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isVisible, users, selectedIndex, onSelectUser, onClose]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isVisible) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isVisible, onClose]);

    if (!isVisible) {
        return null;
    }

    return (
        <div 
            ref={dropdownRef}
            className="user-mention-dropdown"
            style={{
                position: 'fixed', // Use fixed positioning for better mobile support
                top: position?.top || 0,
                left: position?.left || 0,
                zIndex: 1000
            }}
        >
            {isLoading ? (
                <div className="mention-loading">
                    <div className="mention-spinner"></div>
                    <span>Searching users...</span>
                </div>
            ) : (
                <div className="mention-list">
                    {users.map((user, index) => (
                        <div
                            key={user.id}
                            className={`mention-item ${index === selectedIndex ? 'selected' : ''}`}
                            onClick={() => handleUserSelect(user)}
                            onMouseEnter={() => setSelectedIndex(index)}
                        >
                            <div className="mention-avatar">
                                {user.profile_image_url ? (
                                    <img src={user.profile_image_url} alt={user.name} />
                                ) : (
                                    <div className="mention-avatar-placeholder">
                                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                )}
                            </div>
                            <div className="mention-info">
                                <div className="mention-name">{user.name}</div>
                                <div className="mention-username">
                                    {user.username ? `@${user.username}` : user.email}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default UserMentionDropdown; 